import { datafluxRum } from "@cloudcare/browser-rum";

/**
 * 检查错误是否应该被过滤（不上报到观测云）
 * 这个函数用于在 datafluxRum 的 beforeSend 回调中过滤已知的无害错误
 */
export function shouldFilterError(error: any): boolean {
  if (!error) return false;

  const errorMessage = typeof error === 'string' 
    ? error 
    : error?.message || error?.error?.message || '';
  const errorName = error?.name || error?.error?.name || '';
  const errorStack = error?.stack || error?.error?.stack || '';

  // 过滤 React DOM 清理时的 removeChild 错误（页面跳转时常见，不影响业务逻辑）
  if (
    (typeof errorMessage === 'string' &&
      (errorMessage.includes('removeChild') ||
        errorMessage.includes('The node to be removed is not a child of this node'))) ||
    (errorName === 'NotFoundError' &&
      (errorMessage.includes('removeChild') || errorStack.includes('removeChild'))) ||
    (typeof errorStack === 'string' && errorStack.includes('removeChild'))
  ) {
    return true;
  }

  // 过滤 GTM 相关的错误（jQuery 未定义等）
  if (
    (typeof errorMessage === 'string' &&
      (errorMessage.includes('$ is not defined') ||
        errorMessage.includes('jQuery is not defined'))) ||
    (typeof errorStack === 'string' &&
      (errorStack.includes('googletagmanager.com') ||
        errorStack.includes('gtm.js')))
  ) {
    return true;
  }

  // 过滤 Clerk 相关的序列化错误
  if (
    typeof errorMessage === 'string' &&
    (errorMessage.includes('Object Not Found Matching') ||
      errorMessage.includes('MethodName:') ||
      errorMessage.includes('ParamCount:'))
  ) {
    return true;
  }

  // 过滤 RUM session replay 相关的错误
  if (
    (typeof errorMessage === 'string' &&
      (errorMessage.includes('Reached max') ||
        errorMessage.includes('events size queued') ||
        errorMessage.includes('rum-openway.guance.one'))) ||
    (typeof errorStack === 'string' && errorStack.includes('rum-openway.guance.one'))
  ) {
    return true;
  }

  // 过滤 Webpack chunk 加载错误
  if (
    (typeof errorMessage === 'string' &&
      (errorMessage.includes('ChunkLoadError') ||
        errorMessage.includes('Loading chunk') ||
        errorMessage.includes('Loading CSS chunk') ||
        errorMessage.includes('chunk failed'))) ||
    errorName === 'ChunkLoadError'
  ) {
    return true;
  }

  return false;
}

/**
 * 初始化全局错误处理
 * 包括：
 * - window.onerror 处理同步错误
 * - unhandledrejection 处理未捕获的 Promise 拒绝
 * - 过滤 GTM、Clerk、RUM 相关的已知错误
 */
export function setupGlobalErrorHandler() {
  // 添加全局错误处理，捕获空错误消息的情况
  const originalErrorHandler = window.onerror;
  window.onerror = (message, source, lineno, colno, error) => {
    // 过滤 GTM 相关的错误（jQuery 未定义等）
    if (
      typeof message === 'string' &&
      (message.includes('$ is not defined') ||
        message.includes('jQuery is not defined') ||
        source?.includes('googletagmanager.com') ||
        source?.includes('gtm.js'))
    ) {
      // 静默处理 GTM 错误，不影响应用运行
      return true; // 返回 true 表示错误已处理
    }

    // 过滤 Clerk 相关的序列化错误
    if (
      typeof message === 'string' &&
      (message.includes('Object Not Found Matching') ||
        message.includes('MethodName:') ||
        message.includes('ParamCount:') ||
        source?.includes('clerk') ||
        source?.includes('clerk.dev'))
    ) {
      // 静默处理 Clerk 内部错误，不影响应用运行
      return true; // 返回 true 表示错误已处理
    }

    // 过滤 RUM session replay 相关的错误
    if (
      typeof message === 'string' &&
      (message.includes('Reached max') ||
        message.includes('events size queued') ||
        message.includes('rum-openway.guance.one') ||
        source?.includes('rum-openway.guance.one'))
    ) {
      // 静默处理 RUM 内部错误，不影响应用运行
      return true; // 返回 true 表示错误已处理
    }

    // 过滤 React DOM 清理时的 removeChild 错误（页面跳转时常见，不影响业务逻辑）
    if (
      (typeof message === 'string' &&
        (message.includes('removeChild') ||
          message.includes('The node to be removed is not a child of this node'))) ||
      (error?.name === 'NotFoundError' &&
        error?.message?.includes('removeChild')) ||
      (error?.message &&
        typeof error.message === 'string' &&
        error.message.includes('removeChild'))
    ) {
      // 静默处理 DOM 清理错误，不影响应用运行
      return true; // 返回 true 表示错误已处理
    }

    // 过滤 Webpack chunk 加载错误（资源加载失败，通常由缓存或网络问题引起，不影响业务逻辑）
    if (
      (typeof message === 'string' &&
        (message.includes('ChunkLoadError') ||
          message.includes('Loading chunk') ||
          message.includes('Loading CSS chunk') ||
          message.includes('chunk failed'))) ||
      (error?.name === 'ChunkLoadError') ||
      (error?.message &&
        typeof error.message === 'string' &&
        (error.message.includes('ChunkLoadError') ||
          error.message.includes('Loading chunk') ||
          error.message.includes('Loading CSS chunk') ||
          error.message.includes('chunk failed'))) ||
      (source?.includes('.chunk.') && source?.includes('.css'))
    ) {
      // 静默处理 chunk 加载错误，不影响应用运行（通常可通过刷新页面解决）
      return true; // 返回 true 表示错误已处理
    }

    // 如果错误消息为空，尝试从 error 对象中获取更多信息
    if (error && (!error.message || error.message === 'Error')) {
      const errorInfo = {
        message: error.message || String(message) || 'Unknown error',
        stack: error.stack,
        name: error.name,
        source,
        lineno,
        colno,
      };
      // 上报到观测云
      if (typeof datafluxRum?.addError === 'function') {
        try {
          datafluxRum.addError(error, {
            source: 'global_error_handler',
            type: 'empty_error_message',
            errorInfo: JSON.stringify(errorInfo),
          });
        } catch (e) {
          // 静默失败
        }
      }
    }
    // 其他错误继续使用原始处理器
    if (originalErrorHandler) {
      return originalErrorHandler(message, source, lineno, colno, error);
    }
    return false;
  };

  // 处理未捕获的 Promise 拒绝（包括 GTM 和 Clerk 相关错误）
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    // 过滤 GTM 相关的错误
    if (
      reason &&
      (typeof reason === 'string' &&
        (reason.includes('$ is not defined') ||
          reason.includes('jQuery is not defined') ||
          reason.includes('googletagmanager.com'))) ||
      (reason?.message &&
        typeof reason.message === 'string' &&
        (reason.message.includes('$ is not defined') ||
          reason.message.includes('jQuery is not defined')))
    ) {
      // 阻止默认的错误处理
      event.preventDefault();
    }

    // 过滤 Clerk 相关的序列化错误
    if (
      reason &&
      (typeof reason === 'string' &&
        (reason.includes('Object Not Found Matching') ||
          reason.includes('MethodName:') ||
          reason.includes('ParamCount:'))) ||
      (reason?.message &&
        typeof reason.message === 'string' &&
        (reason.message.includes('Object Not Found Matching') ||
          reason.message.includes('MethodName:') ||
          reason.message.includes('ParamCount:')))
    ) {
      // 阻止默认的错误处理
      event.preventDefault();
    }

    // 过滤 RUM session replay 相关的错误
    if (
      reason &&
      (typeof reason === 'string' &&
        (reason.includes('Reached max') ||
          reason.includes('events size queued') ||
          reason.includes('rum-openway.guance.one'))) ||
      (reason?.message &&
        typeof reason.message === 'string' &&
        (reason.message.includes('Reached max') ||
          reason.message.includes('events size queued') ||
          reason.message.includes('rum-openway.guance.one')))
    ) {
      // 阻止默认的错误处理
      event.preventDefault();
    }

    // 过滤 React DOM 清理时的 removeChild 错误（页面跳转时常见，不影响业务逻辑）
    if (
      reason &&
      ((typeof reason === 'string' &&
        (reason.includes('removeChild') ||
          reason.includes('The node to be removed is not a child of this node'))) ||
        (reason?.name === 'NotFoundError' &&
          reason?.message?.includes('removeChild')) ||
        (reason?.message &&
          typeof reason.message === 'string' &&
          reason.message.includes('removeChild')))
    ) {
      // 阻止默认的错误处理
      event.preventDefault();
    }

    // 过滤 Webpack chunk 加载错误（资源加载失败，通常由缓存或网络问题引起，不影响业务逻辑）
    if (
      reason &&
      ((typeof reason === 'string' &&
        (reason.includes('ChunkLoadError') ||
          reason.includes('Loading chunk') ||
          reason.includes('Loading CSS chunk') ||
          reason.includes('chunk failed'))) ||
        (reason?.name === 'ChunkLoadError') ||
        (reason?.message &&
          typeof reason.message === 'string' &&
          (reason.message.includes('ChunkLoadError') ||
            reason.message.includes('Loading chunk') ||
            reason.message.includes('Loading CSS chunk') ||
            reason.message.includes('chunk failed'))))
    ) {
      // 阻止默认的错误处理（通常可通过刷新页面解决）
      event.preventDefault();
    }
  });
}

